import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { SMOOTHING_ALGORITHMS, SMOOTHING_DEFAULTS } from '@/tools/signalSmoothing/signalSmoothing.constants';
import type { BaseToolPersistedState, ToolItem } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type ValueWithUnits = ValueWithUnitsItem & { valid?: boolean };
type InitialValueWithUnits = Partial<ValueWithUnits>;

interface SignalSmoothingState extends BaseToolPersistedState {
  inputSignal?: ToolItem | null;
  color: string;
  algorithmSelectedValue: string;
  samplingRate: InitialValueWithUnits;
  smoothingWindow: InitialValueWithUnits;
  cutoff: InitialValueWithUnits;
  polynomialFactor: number;
  isSamplingRateAuto: boolean;
  isCutoffAuto: boolean;
  isTau: boolean;
  tau: InitialValueWithUnits;
  alpha: number;
  isMigratedLowPassFilter: boolean;
}

export type SignalSmoothingDehydratedState = SignalSmoothingState;

export class SignalSmoothingStore extends BaseToolStore {
  static readonly storeName = 'sqSignalSmoothingStore';
  type = TREND_TOOLS.SIGNAL_SMOOTHING;
  parameterDefinitions = {
    inputSignal: { predicate: ['name', 'inputSignal'] },
  };

  initialize(): void {
    const initialState: SignalSmoothingState = {
      ...BASE_TOOL_COMMON_PROPS,
      algorithmSelectedValue: SMOOTHING_ALGORITHMS.SEEQ_AGILE.VALUE,
      samplingRate: { value: undefined, units: undefined },
      smoothingWindow: { value: undefined, units: undefined },
      cutoff: { value: undefined, units: undefined },
      polynomialFactor: SMOOTHING_DEFAULTS.POLYNOMIAL_FACTOR,
      isSamplingRateAuto: true,
      isCutoffAuto: true,
      isTau: true,
      tau: SMOOTHING_DEFAULTS.TAU,
      alpha: SMOOTHING_DEFAULTS.ALPHA,
      isMigratedLowPassFilter: false,
      color: '',
    };
    this.state = this.immutable(initialState);
  }

  get color(): string {
    return this.state.get('color');
  }

  get inputSignal(): ToolItem & { id: string } {
    return this.state.get('inputSignal');
  }

  get algorithmSelectedValue(): string {
    return this.state.get('algorithmSelectedValue');
  }

  get samplingRate(): ValueWithUnits {
    return this.state.get('samplingRate');
  }

  get smoothingWindow(): ValueWithUnits {
    return this.state.get('smoothingWindow');
  }

  get cutoff(): ValueWithUnits {
    return this.state.get('cutoff');
  }

  get polynomialFactor(): number {
    return this.state.get('polynomialFactor');
  }

  get isSamplingRateAuto(): boolean {
    return this.state.get('isSamplingRateAuto');
  }

  get isCutoffAuto(): boolean {
    return this.state.get('isCutoffAuto');
  }

  get isTau(): boolean {
    return this.state.get('isTau');
  }

  get tau(): ValueWithUnits {
    return this.state.get('tau');
  }

  get alpha(): number {
    return this.state.get('alpha');
  }

  get isMigratedLowPassFilter(): boolean {
    return this.state.get('isMigratedLowPassFilter');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): SignalSmoothingDehydratedState {
    return {
      ...this.getBasePersistedState(),
      inputSignal: this.state.get('inputSignal') as ToolItem | null | undefined,
      color: this.state.get('color') as string,
      algorithmSelectedValue: this.state.get('algorithmSelectedValue') as string,
      samplingRate: this.state.get('samplingRate') as ValueWithUnits,
      smoothingWindow: this.state.get('smoothingWindow') as ValueWithUnits,
      cutoff: this.state.get('cutoff') as ValueWithUnits,
      polynomialFactor: this.state.get('polynomialFactor') as number,
      isSamplingRateAuto: Boolean(this.state.get('isSamplingRateAuto')),
      isCutoffAuto: Boolean(this.state.get('isCutoffAuto')),
      isTau: Boolean(this.state.get('isTau')),
      tau: this.state.get('tau') as ValueWithUnits,
      alpha: this.state.get('alpha') as number,
      isMigratedLowPassFilter: Boolean(this.state.get('isMigratedLowPassFilter')),
    };
  }

  /**
   * Sets the references panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<SignalSmoothingDehydratedState>): void {
    this.applyBasePersistedState(dehydratedState);

    if ('inputSignal' in dehydratedState) {
      this.state.set('inputSignal', dehydratedState.inputSignal);
    }
    if ('color' in dehydratedState && dehydratedState.color !== undefined) {
      this.state.set('color', dehydratedState.color);
    }
    if ('algorithmSelectedValue' in dehydratedState && dehydratedState.algorithmSelectedValue !== undefined) {
      this.state.set('algorithmSelectedValue', dehydratedState.algorithmSelectedValue);
    }
    if ('samplingRate' in dehydratedState && dehydratedState.samplingRate !== undefined) {
      this.state.set('samplingRate', dehydratedState.samplingRate);
    }
    if ('smoothingWindow' in dehydratedState && dehydratedState.smoothingWindow !== undefined) {
      this.state.set('smoothingWindow', dehydratedState.smoothingWindow);
    }
    if ('cutoff' in dehydratedState && dehydratedState.cutoff !== undefined) {
      this.state.set('cutoff', dehydratedState.cutoff);
    }
    if ('polynomialFactor' in dehydratedState && dehydratedState.polynomialFactor !== undefined) {
      this.state.set('polynomialFactor', dehydratedState.polynomialFactor);
    }
    if ('isSamplingRateAuto' in dehydratedState && dehydratedState.isSamplingRateAuto !== undefined) {
      this.state.set('isSamplingRateAuto', dehydratedState.isSamplingRateAuto);
    }
    if ('isCutoffAuto' in dehydratedState && dehydratedState.isCutoffAuto !== undefined) {
      this.state.set('isCutoffAuto', dehydratedState.isCutoffAuto);
    }
    if ('isTau' in dehydratedState && dehydratedState.isTau !== undefined) {
      this.state.set('isTau', dehydratedState.isTau);
    }
    if ('tau' in dehydratedState && dehydratedState.tau !== undefined) {
      this.state.set('tau', dehydratedState.tau);
    }
    if ('alpha' in dehydratedState && dehydratedState.alpha !== undefined) {
      this.state.set('alpha', dehydratedState.alpha);
    }
    if ('isMigratedLowPassFilter' in dehydratedState && dehydratedState.isMigratedLowPassFilter !== undefined) {
      this.state.set('isMigratedLowPassFilter', dehydratedState.isMigratedLowPassFilter);
    }
  }

  protected readonly handlers = {
    ...this.baseHandlers,

    SIGNAL_SMOOTHING_SET_ALGORITHM_SELECTED_VALUE: ({ algorithmSelectedValue }: { algorithmSelectedValue: string }) => {
      this.state.set('algorithmSelectedValue', algorithmSelectedValue);
    },

    SIGNAL_SMOOTHING_SET_SAMPLING_RATE: ({ samplingRate }: { samplingRate: { value?: number; units?: string } }) => {
      this.state.set('samplingRate', samplingRate);
    },

    SIGNAL_SMOOTHING_SET_SMOOTHING_WINDOW: ({
      smoothingWindow,
    }: {
      smoothingWindow: { value?: number; units?: string };
    }) => {
      this.state.set('smoothingWindow', smoothingWindow);
    },

    SIGNAL_SMOOTHING_SET_CUTOFF: ({ cutoff }: { cutoff: { value?: number; units?: string } }) => {
      this.state.set('cutoff', cutoff);
    },

    SIGNAL_SMOOTHING_SET_POLYNOMIAL_FACTOR: ({ polynomialFactor }: { polynomialFactor: number }) => {
      this.state.set('polynomialFactor', polynomialFactor);
    },

    SIGNAL_SMOOTHING_SET_IS_SAMPLE_RATE_AUTO: ({ isSamplingRateAuto }: { isSamplingRateAuto: boolean }) => {
      this.state.set('isSamplingRateAuto', isSamplingRateAuto);
    },

    SIGNAL_SMOOTHING_SET_IS_CUTOFF_AUTO: ({ isCutoffAuto }: { isCutoffAuto: boolean }) => {
      this.state.set('isCutoffAuto', isCutoffAuto);
    },

    SIGNAL_SMOOTHING_SET_IS_TAU: ({ isTau }: { isTau: boolean }) => {
      this.state.set('isTau', isTau);
    },

    SIGNAL_SMOOTHING_SET_TAU: ({ tau }: { tau: ValueWithUnits }) => {
      this.state.set('tau', tau);
    },

    SIGNAL_SMOOTHING_SET_ALPHA: ({ alpha }: { alpha: number }) => {
      this.state.set('alpha', alpha);
    },

    SIGNAL_SMOOTHING_SET_COLOR: ({ color }: { color: string }) => {
      this.state.set('color', color);
    },

    SIGNAL_SMOOTHING_SET_IS_MIGRATED_LOW_PASS_FILTER: ({
      isMigratedLowPassFilter,
    }: {
      isMigratedLowPassFilter: boolean;
    }) => {
      this.state.set('isMigratedLowPassFilter', isMigratedLowPassFilter);
    },
  };
}
